import React, { FC, useEffect, useMemo, useState } from "react";
import { FormikProps } from "formik";
import Select from "react-select";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useAppDispatch } from "src/redux/storeHooks";
import { getCustomFieldsListAsync, getCustomFieldValueListAsync } from "src/redux/actions/omsPageActions";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { FontSize } from "ckeditor5";
import { components } from "react-select";
import { Card, CardContent, Tabs, Tab, Box, styled, } from "@mui/material";
import InventoryLogs from "../inventory/InventoryLogs";
import PriceLogs from "../price-logs/PriceLogs";

interface ProductCustomFieldsProps {
  values: any;
  setFormikState: FormikProps<any>["setFormikState"];
  mode: "create" | "edit";
  productRowData?: any;
}

const CheckboxOption = (props: any) => {
  const { isSelected, label } = props;

  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          style={{ pointerEvents: "none" }} // keep react-select in control
        />
        <span>{label}</span>
      </div>
    </components.Option>
  );
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTabs-indicator": {
    backgroundColor: "#007BFF", // bright blue underline
    height: 3,
    borderRadius: 2,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "15px",
  textTransform: "none",
  color: "#007BFF",
  "&.Mui-selected": {
    color: "#007BFF",
  },
  "&:hover": {
    color: "#0056b3",
  },
  minHeight: 48,
  marginRight: theme.spacing(2),
}));

const parseCheckboxValue = (input: any): boolean => {
  if (typeof input === "boolean") return input;

  if (typeof input === "number") return input === 1;

  if (typeof input === "string") {
    const normalized = input.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }

  if (input && typeof input === "object") {
    const raw =
      input?.value ??
      input?.name ??
      input?.label ??
      input?.api_name ??
      input?.field_value;

    return parseCheckboxValue(raw);
  }

  return false;
};

const ProductCustomFields: FC<ProductCustomFieldsProps> = ({ values, setFormikState, mode }) => {
  const dispatch = useAppDispatch();

  const isProductDetailPage = true;

  //  master meta list (id, display_name, input_type, owner, isActive...)
  const { customFieldsList } = useSelector((state: any) => state.omsCustomFieldsSlice);

  //  fieldsMeta is what we render (use master list for edit; for create we fetch list if needed)
  const [fieldsMeta, setFieldsMeta] = useState<any[]>([]);

  const [paginationData] = useState({
    page_num: 1,
    page_limit: 100,
    search: "",
    sort_order: "",
    sort_by: "",
    isActive: true,
  });

  // value options for SELECT/MULTISELECT per fieldId
  const [customFieldValuesById, setCustomFieldValuesById] = useState<Record<number, any[]>>({});
  const [loadingFieldValues, setLoadingFieldValues] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const extractList = (res: any): any[] => {
    return (
      res?.data?.results?.data || res?.results?.data || res?.data?.data ||
      res?.data || res?.results || res || []
    )
  };

  // -------------------------------
  // 1) LOAD META
  // -------------------------------
  useEffect(() => {
    const run = async () => {
      try {
        // CREATE MODE: fetch list (if your store doesn’t already have it)
        const res = await dispatch(
          getCustomFieldsListAsync({
            paramData: {
              page: paginationData.page_num, limit: paginationData.page_limit, search: paginationData.search,
              isActive: paginationData.isActive, sort_by: paginationData.sort_by, sort_order: paginationData.sort_order,
              owner: "PRODUCT",
            },
          })
        ).unwrap();

        const list = extractList(res);
        setFieldsMeta(list);

        // ensure keys exist in formik
        setFormikState((prev: any) => {
          const existing = prev.values.custom_fields || {};
          const next = { ...existing };

          (list || []).forEach((f: any) => {
            if (next[f.id] !== undefined) return;
            if (f.input_type === "MULTISELECT") next[f.id] = [];
            else if (f.input_type === "SELECT") next[f.id] = "";
            else if (f.input_type === "CHECKBOX") next[f.id] = parseCheckboxValue(f.default_value);
            else next[f.id] = "";
          });

          return { ...prev, values: { ...prev.values, custom_fields: next } };
        });
      } catch (err: any) {
        console.error(err);
        const errStatus = err?.status || err?.data?.status || err?.response?.status;
        if (errStatus === 404) {
          setFieldsMeta([]);
        } else {
          toast.error(err?.data?.message || err?.message || "Failed to load custom fields meta");
        }
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, customFieldsList?.length]);

  // -------------------------------
  // 2) PREFILL FORM VALUES
  //    - create: set defaults
  //    - edit: map product_custom_fields_raw -> custom_fields
  // -------------------------------
  useEffect(() => {
    if (!customFieldsList?.length) return;

    // CREATE: set defaults only if not present
    if (mode === "create") {
      setFormikState((prev: any) => {
        const existing = prev.values.custom_fields || {};
        const next = { ...existing };

        customFieldsList.forEach((f: any) => {
          if (next[f.id] !== undefined) return;

          if (f.input_type === "MULTISELECT") {
            const arr = Array.isArray(f.default_value) ? f.default_value : (f.default_value && typeof f.default_value === 'object' ? [f.default_value] : []);
            next[f.id] = arr.map((x: any) => x.id);
          } else if (f.input_type === "SELECT") {
            const arr = Array.isArray(f.default_value) ? f.default_value : (f.default_value && typeof f.default_value === 'object' ? [f.default_value] : []);
            next[f.id] = arr[0] ? arr[0].id : "";
          } else if (f.input_type === "CHECKBOX") {
            next[f.id] = parseCheckboxValue(f.default_value);
          } else {
            next[f.id] = f.default_value?.name ?? "";
          }
        });

        return {
          ...prev,
          values: {
            ...prev.values,
            custom_fields: next,
          },
        };
      });
      return;
    }

    // EDIT: map values.product_custom_fields_raw to formik
    const rawRows: any[] = Array.isArray(values?.product_custom_fields_raw)
      ? values.product_custom_fields_raw
      : [];

    if (!rawRows.length) {
      // still ensure keys exist so inputs render empty
      setFormikState((prev: any) => {
        const existing = prev.values.custom_fields || {};
        const next = { ...existing };
        customFieldsList.forEach((f: any) => {
          if (next[f.id] !== undefined) return;
          if (f.input_type === "MULTISELECT") next[f.id] = [];
          else if (f.input_type === "SELECT") next[f.id] = "";
          else if (f.input_type === "CHECKBOX") next[f.id] = parseCheckboxValue(f.default_value);
          else next[f.id] = "";
        });
        return { ...prev, values: { ...prev.values, custom_fields: next } };
      });
      return;
    }

    // Build a map: platform_field -> row
    const rowByFieldId = new Map<number, any>();
    rawRows.forEach((r: any) => {
      const fid = Number(
        r?.platform_field ??
        r?.platform_field_id ??
        r?.field_id ??
        r?.platform_field?.id
      );
      if (fid) rowByFieldId.set(fid, r);
    });

    setFormikState((prev: any) => {
      const existing = prev.values.custom_fields || {};
      const next = { ...existing };

      customFieldsList.forEach((f: any) => {
        const fieldId = Number(f.id);
        if (!fieldId) return;

        const row = rowByFieldId.get(fieldId);

        // Ensure the key exists even if no row
        if (next[fieldId] === undefined) {
          if (f.input_type === "MULTISELECT") {
            const arr = Array.isArray(f.default_value) ? f.default_value : (f.default_value && typeof f.default_value === 'object' ? [f.default_value] : []);
            next[fieldId] = arr.map((x: any) => x.id);
          } else if (f.input_type === "SELECT") {
            const arr = Array.isArray(f.default_value) ? f.default_value : (f.default_value && typeof f.default_value === 'object' ? [f.default_value] : []);
            next[fieldId] = arr[0] ? arr[0].id : "";
          } else if (f.input_type === "CHECKBOX") {
            next[fieldId] = parseCheckboxValue(f.default_value);
          } else {
            next[fieldId] = f.default_value?.name ?? "";
          }
        }

        if (!row) return;

        // ✅ Your rule:
        // if SELECT/MULTISELECT -> use platform_field_data_ids (else fallback platform_field_data)
        // else -> use api_code
        if (f.input_type === "MULTISELECT") {
          let ids = row?.platform_field_data_ids;

          // normalize ids: array | "1,2,3" | single number | null
          if (typeof ids === "string") {
            ids = ids
              .split(",")
              .map((x: string) => x.trim())
              .filter(Boolean)
              .map((x: string) => Number(x));
          } else if (Array.isArray(ids)) {
            ids = ids.map((x: any) => Number(x)).filter((n: any) => !Number.isNaN(n));
          } else if (ids != null) {
            ids = [Number(ids)].filter((n: any) => !Number.isNaN(n));
          } else if (row?.platform_field_data != null) {
            // fallback
            const one = Number(row.platform_field_data);
            ids = Number.isNaN(one) ? [] : [one];
          } else {
            ids = [];
          }

          // Deduplicate — backend may return duplicate IDs
          next[fieldId] = [...new Set(ids)];
          return;
        }

        if (f.input_type === "SELECT") {
          // Use platform_field_data_id or platform_field_data (singular), NOT platform_field_data_ids
          const id = row?.platform_field_data_id ?? row?.platform_field_data;
          next[fieldId] = id != null ? String(id) : "";
          return;
        }

        // other input types -> api_code (fallback)
        if (f.input_type === "CHECKBOX") {
          // if backend stores checkbox in api_code ("true"/"false") handle it
          const raw =
            row?.api_code ??
            row?.api_name ??
            row?.value ??
            row?.field_value ??
            row?.custom_field_value;

          next[fieldId] = parseCheckboxValue(raw);
          return;
        }

        next[fieldId] = row?.api_code ?? row?.api_name ?? row?.value ?? "";
      });

      return {
        ...prev,
        values: {
          ...prev.values,
          custom_fields: next,
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, customFieldsList?.length, values?.product_custom_fields_raw]);

  // -------------------------------
  // 3) LOAD VALUE LISTS FOR SELECT/MULTISELECT
  // -------------------------------
  useEffect(() => {
    const selectableFields = (fieldsMeta || []).filter(
      (f: any) =>
        (f.input_type === "SELECT" || f.input_type === "MULTISELECT") &&
        f.isActive &&
        f.owner === "PRODUCT"
    );

    if (!selectableFields.length) return;

    selectableFields.forEach((field: any) => {
      const fieldId = Number(field.id);
      if (!fieldId) return;

      if (customFieldValuesById[fieldId]?.length) return;
      if (loadingFieldValues[fieldId]) return;

      setLoadingFieldValues((prev) => ({ ...prev, [fieldId]: true }));

      dispatch(getCustomFieldValueListAsync({ id: fieldId }))
        .unwrap()
        .then((res: any) => {
          const list = extractList(res);
          const normalized = (list || [])
            .map((x: any) => ({
              id: x?.id ?? x?.value_id ?? x?.platform_value_id,
              name: x?.name ?? x?.api_code ?? x?.value ?? x?.display_name ?? x?.label,
            }))
            .filter((x: any) => x.id != null);

          setCustomFieldValuesById((prev) => ({ ...prev, [fieldId]: normalized }));
        })
        .catch((err: any) => {
          console.error(err);
        })
        .finally(() => {
          setLoadingFieldValues((prev) => ({ ...prev, [fieldId]: false }));
        });
    });
  }, [dispatch, fieldsMeta, customFieldValuesById, loadingFieldValues]);

  const hasActiveProductFields = useMemo(() => {
    return (fieldsMeta || []).some((f: any) => f.isActive && f.owner === "PRODUCT");
  }, [fieldsMeta]);

  const updateCustomFieldValue = (id: number, value: any) => {
    setFormikState((prev: any) => ({
      ...prev,
      values: {
        ...prev.values,
        custom_fields: {
          ...(prev.values.custom_fields || {}),
          [id]: value,
        },
      },
    }));
  };

  const renderCustomFieldInput = (field: any) => {
    const value = values.custom_fields?.[field.id];

    switch (field.input_type) {
      case "TEXT":
        return (
          <input
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "NUMBER":
        return (
          <input
            type="number"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "EMAIL":
        return (
          <input
            type="email"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "DATE":
        return (
          <input
            type="date"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "DATE_TIME":
        return (
          <input
            type="datetime-local"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "CHECKBOX":
        return (
          <input
            type="checkbox"
            checked={parseCheckboxValue(value)}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.checked)}
          />
        );

      case "URL":
        return (
          <input
            type="url"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
            placeholder="https://..."
          />
        );

      case "TIME":
        return (
          <input
            type="time"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "SELECT": {
        const options = (customFieldValuesById[field.id] || []).map((o: any) => ({
          value: String(o.id),
          label: o.name,
        }));

        const selected = options.find((o: any) => String(o.value) === String(value ?? "")) || null;

        return (
          <Select
            classNamePrefix="rs"
            options={options}
            value={selected}
            onChange={(opt: any) => updateCustomFieldValue(field.id, opt?.value || "")}
            placeholder="Select…"
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed" maxMenuHeight={260}
            menuShouldScrollIntoView={false}
          />
        );
      }

      case "MULTISELECT": {
        const options = (customFieldValuesById[field.id] || []).map((o: any) => ({
          value: o.id,
          label: o.name,
        }));

        const selected = options.filter((o: any) => (value ?? []).includes(o.value));

        return (
          <Select
            classNamePrefix="rs"
            isMulti
            options={options}
            value={selected}
            onChange={(items: any) =>
              updateCustomFieldValue(field.id, (items || []).map((x: any) => x.value))
            }
            placeholder="Select…"
            components={{ Option: CheckboxOption }}   // ✅ checkbox UI
            closeMenuOnSelect={false}                 // ✅ keep menu open when clicking
            hideSelectedOptions={false}               // ✅ still show options with checkbox checked
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement="auto"
            maxMenuHeight={260}
            menuShouldScrollIntoView={false}
          />
        );
      }

      default:
        return (
          <input
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );
    }
  };

  if (!hasActiveProductFields) return null;

  return (
    <>
      <div className="card card-flush">
        <div className="card-headerP d-flex justify-content-between align-items-center">
          <div className="card-titleP">
            <h2 className="mb-0">Custom Fields</h2>
          </div>
        </div>

        <div className="card-bodyP pt-0 mt-2">
          <div className="cfGrid">
            {fieldsMeta
              ?.filter((f: any) => f.isActive && f.owner === "PRODUCT")
              .map((field: any) => {
                const tooltipText = field?.description || field?.help_text || field?.api_description || field?.notes || "-";

                return (
                  <div key={field.id} className="cfTile">
                    <div className="cfTile__head">
                      <div className="d-flex align-items-center justify-content-between">

                        <div className="fw-bold text-primary d-flex align-items-center gap-3">
                          {field.display_name}
                          {field.isRequired ? <span className="text-danger">*</span> : null}
                          {/* <small className="text-muted" style={{ fontWeight: "500", fontSize: "10px" }}>
                          {field.input_type}
                        </small> */}

                          <OverlayTrigger placement="top" overlay={<Tooltip>{tooltipText}</Tooltip>} >
                            <span className="cfInfoIcon"> <i className="fa fa-info-circle" /> </span>
                          </OverlayTrigger>
                        </div>
                      </div>
                    </div>

                    <div className="cfTile__body">{renderCustomFieldInput(field)}</div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {mode === "edit" && !(values.isProductHasVariant || values.isproductHasVariant) && (
        <Box sx={{ width: "100%", mt: 3 }}>
          <Card
            sx={{
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              border: "1px solid #ddd",
            }}
          >
            <Box sx={{ borderBottom: "1px solid #e0e0e0", px: 2, pt: 1 }}>
              <StyledTabs value={activeTab} onChange={handleChange}>
                <StyledTab label="Inventory Logs" />
                <StyledTab label="Price Logs" />
              </StyledTabs>
            </Box>

            <CardContent sx={{ p: 2 }}>
              <>
                {activeTab === 0 && (
                  <InventoryLogs 
                    isProductDetailPage={isProductDetailPage} 
                    variantId={values.varients_data?.[0]?.variant_id}
                  />
                )}
                {activeTab === 1 && (
                  <PriceLogs 
                    isProductDetailPage={isProductDetailPage} 
                    variantId={values.varients_data?.[0]?.variant_id}
                  />
                )}
              </>
            </CardContent>
          </Card>
        </Box>
      )}
    </>
  );
};

export default ProductCustomFields;
